import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Controller, FormProvider, useForm, useWatch, type Path } from "react-hook-form";
import { Send, UserPlus } from "lucide-react";
import {
  Alert,
  Button,
  Card,
  Combobox,
  DatePicker,
  ErrorState,
  Input,
  LoadingState,
  Page,
  Select,
  Text,
  Textarea,
} from "@marekl/adminer/components";
import { UnsavedChangesModal, useSetTopbarTitle, useUnsavedChangesGuard } from "@marekl/adminer/shell";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { useCurrentCompany } from "../../lib/company";
import {
  PAYMENT_LABEL,
  documentPath,
  previewTotals,
  useDocument,
  useDocumentCopy,
  useIssueDocument,
  useSaveDocument,
  type Document,
  type DocumentCreate,
  type DocumentType,
  type PaymentType,
  type Totals,
} from "../../lib/queries/documents";
import { searchContacts, useContact, type Contact } from "../../lib/queries/contacts";
import { LineItemsEditor, newItem, type ItemValues } from "../../components/LineItemsEditor";
import { DecimalInput } from "../../components/DecimalInput";
import { ContactModal } from "../ContactsPage";
import { addDaysCivil, civilToDayjs, dayjsToCivil, todayCivil } from "../../lib/dates";
import { useNextNumber } from "../../lib/queries/series";
import { docTitles } from "../DocumentsPage/DocumentsPage";
import styles from "./DocumentEditorPage.module.css";

interface EditorValues {
  /** The number to issue under; not part of the draft. */
  number: string;
  payment_type: PaymentType;
  currency: "CZK" | "EUR";
  czk_rate: number | null;
  contact_id: string | null;
  issue_date: string;
  taxable_supply_date: string | null;
  due_date: string | null;
  expiration_date: string | null;
  order_number: string;
  items_text_prefix: string;
  items_text_suffix: string;
  credit_note_reason: string;
  items: ItemValues[];
}

const EDITABLE_TYPES: DocumentType[] = ["sales_invoice", "purchase_invoice", "sales_order", "retail"];

function fromDocument(d: Document | DocumentCreate): Omit<EditorValues, "number"> {
  return {
    payment_type: d.payment_type,
    currency: d.currency,
    czk_rate: d.czk_rate ?? null,
    contact_id: d.contact_id ?? null,
    issue_date: d.issue_date,
    taxable_supply_date: d.taxable_supply_date ?? null,
    due_date: d.due_date ?? null,
    expiration_date: d.expiration_date ?? null,
    order_number: d.order_number ?? "",
    items_text_prefix: d.items_text_prefix ?? "",
    items_text_suffix: d.items_text_suffix ?? "",
    credit_note_reason: d.credit_note_reason ?? "",
    items: d.items.map((it) => ({
      product_id: it.product_id ?? null,
      name: it.name,
      unit: it.unit ?? "",
      quantity: it.quantity,
      unit_price: it.unit_price,
      vat_rate: it.vat_rate,
      incl_vat: it.incl_vat,
    })),
  };
}

function toInput(type: DocumentType, v: EditorValues): DocumentCreate {
  return {
    type,
    payment_type: v.payment_type,
    currency: v.currency,
    czk_rate: v.currency === "CZK" ? null : v.czk_rate,
    contact_id: v.contact_id,
    issue_date: v.issue_date,
    taxable_supply_date: v.taxable_supply_date,
    due_date: v.due_date,
    expiration_date: v.expiration_date,
    order_number: v.order_number.trim(),
    items_text_prefix: v.items_text_prefix,
    items_text_suffix: v.items_text_suffix,
    credit_note_reason: v.credit_note_reason.trim(),
    items: v.items.map((it) => ({
      product_id: it.product_id,
      name: it.name.trim(),
      unit: it.unit.trim(),
      quantity: it.quantity ?? 0,
      unit_price: it.unit_price ?? 0,
      vat_rate: it.vat_rate,
      incl_vat: it.incl_vat,
    })),
  };
}

/** Lines the server can total: named, with a quantity and a price. */
function completeItems(items: ItemValues[]) {
  const idx: number[] = [];
  const out = items.flatMap((it, i) => {
    if (it.quantity === null || it.unit_price === null) return [];
    idx.push(i);
    return [{ name: it.name || "-", quantity: it.quantity, unit_price: it.unit_price, vat_rate: it.vat_rate, incl_vat: it.incl_vat }];
  });
  return { idx, out };
}

/** Debounced server totals for what is on screen (one money implementation). */
function useLiveTotals(companyId: string, items: ItemValues[]) {
  // idx: which item each server line belongs to; sigs: what that item held
  // when it was sent, so a row moved or edited since shows no stale total.
  const [totals, setTotals] = useState<{ t: Totals; idx: number[]; sigs: string[] }>();
  const [pending, setPending] = useState(false);
  const key = JSON.stringify(items);
  const seq = useRef(0);
  useEffect(() => {
    const { idx, out } = completeItems(items);
    if (out.length === 0) {
      // A reply still on its way belongs to rows that are gone.
      seq.current++;
      setTotals(undefined);
      setPending(false);
      return;
    }
    const mine = ++seq.current;
    setPending(true);
    const timer = window.setTimeout(() => {
      previewTotals(companyId, out)
        .then((t) => {
          if (mine === seq.current) setTotals({ t, idx, sigs: idx.map((i) => JSON.stringify(items[i])) });
        })
        // A rejected preview (an amount out of range) has no totals to show.
        .catch(() => {
          if (mine === seq.current) setTotals(undefined);
        })
        .finally(() => {
          if (mine === seq.current) setPending(false);
        });
    }, 350);
    return () => window.clearTimeout(timer);
    // key stands for items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, key]);
  // A row shows its server total only while it is still the row that was
  // sent: moved or edited since, it waits for the recount.
  const lineTotals = useMemo(() => {
    const out: (number | undefined)[] = items.map(() => undefined);
    totals?.idx.forEach((itemIndex, k) => {
      if (JSON.stringify(items[itemIndex]) === totals.sigs[k]) out[itemIndex] = totals.t.lines[k]?.incl_vat;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals, key]);
  return { totals: totals?.t, lineTotals, pending };
}

function ContactPicker({ companyId, value, onChange, error, label }: {
  companyId: string;
  value: string | null;
  onChange: (id: string | null) => void;
  error?: string;
  label: string;
}) {
  const current = useContact(companyId, value);
  const [creating, setCreating] = useState(false);
  const [extra, setExtra] = useState<Contact[]>([]);
  const known = [...(current.data ? [current.data] : []), ...extra];
  return (
    <>
      <Combobox<Contact>
        label={label}
        required
        loadOptions={async (q) => {
          const found = await searchContacts(companyId, q);
          return [...known.filter((k) => !found.some((f) => f.id === k.id)), ...found];
        }}
        getOptionLabel={(c) => c.billing_details.business_name}
        getOptionValue={(c) => c.id}
        renderOption={(c) => (
          <span>
            {c.billing_details.business_name}
            {c.billing_details.ico && <small> · IČO {c.billing_details.ico}</small>}
          </span>
        )}
        value={value}
        onChange={onChange}
        error={error}
        suffix={
          <Button type="button" size="sm" weight="ghost" iconOnly aria-label="Nový kontakt" startIcon={<UserPlus size={15} />} onClick={() => setCreating(true)} />
        }
      />
      {creating && (
        <ContactModal
          onClose={() => setCreating(false)}
          onSaved={(c) => {
            setExtra((x) => [c, ...x]);
            onChange(c.id);
          }}
        />
      )}
    </>
  );
}

/**
 * doc: the draft being edited; copy: a new document's content copied from
 * another (iDoklad "kopie faktury") — stored only when saved.
 */
function Editor({ type, doc, copy }: { type: DocumentType; doc?: Document; copy?: { from: string; input: DocumentCreate } }) {
  const { id: companyId, company } = useCurrentCompany();
  const navigate = useNavigate();
  const save = useSaveDocument(companyId);
  const issue = useIssueDocument(companyId);
  const vatPayer = type === "purchase_invoice" ? true : company.billing_details.vat_payer;
  const defaultRate = company.billing_details.vat_payer ? company.default_vat_rate : 0;
  const today = todayCivil();
  const invoiceLike = type !== "sales_order";
  const titles = docTitles(type, company.billing_details.vat_payer);
  useSetTopbarTitle(titles.main);

  const form = useForm<EditorValues>({
    mode: "onTouched",
    defaultValues: {
      number: "",
      ...(doc
        ? fromDocument(doc)
        : copy
        ? fromDocument(copy.input)
        : {
            payment_type: type === "retail" ? "cash" : company.default_payment_type,
            currency: "CZK",
            czk_rate: null,
            contact_id: null,
            issue_date: today,
            taxable_supply_date: invoiceLike && company.billing_details.vat_payer ? today : null,
            due_date: invoiceLike && type !== "retail" ? addDaysCivil(today, company.default_due_days) : null,
            expiration_date: invoiceLike ? null : addDaysCivil(today, company.default_quote_validity_days),
            order_number: "",
            items_text_prefix: "",
            items_text_suffix: "",
            credit_note_reason: "",
            items: [newItem(defaultRate)],
          }),
    },
  });
  const { control, register, handleSubmit, setError, setValue, formState, reset, getValues } = form;
  const [items, currency, issueDate, contactId, number] = useWatch({ control, name: ["items", "currency", "issue_date", "contact_id", "number"] });
  const contact = useContact(companyId, contactId ?? undefined);
  const [editNumber, setEditNumber] = useState(false);
  const live = useLiveTotals(companyId, items);
  const [leaveTo, setLeaveTo] = useState<string>();
  const [savedId, setSavedId] = useState<string | undefined>(doc?.id);
  const guard = useUnsavedChangesGuard(formState.isDirty && !leaveTo);

  // "Číslo dokladu": the next number of the issue year's series, editable —
  // a changed one is issued as a manual number (legacy NewReceiptNumberInput).
  const year = Number((issueDate || today).slice(0, 4));
  const next = useNextNumber(companyId, type, year, true);
  const suggested = useRef("");
  useEffect(() => {
    const n = next.data?.number ?? "";
    if (getValues("number") === suggested.current) setValue("number", n);
    suggested.current = n;
  }, [next.data?.number, getValues, setValue]);

  // Legacy decorator: the issue date carries the due date and DUZP with it.
  const firstIssue = useRef(issueDate);
  useEffect(() => {
    if (!issueDate || issueDate === firstIssue.current) return;
    firstIssue.current = issueDate;
    if (invoiceLike && type !== "retail") setValue("due_date", addDaysCivil(issueDate, company.default_due_days), { shouldDirty: true });
    if (invoiceLike && vatPayer) setValue("taxable_supply_date", issueDate, { shouldDirty: true });
  }, [issueDate, invoiceLike, type, vatPayer, company.default_due_days, setValue]);

  // Navigate once the saved values are the form's baseline (guard trap 2).
  useEffect(() => {
    if (leaveTo && !formState.isDirty) navigate(leaveTo);
  }, [leaveTo, formState.isDirty, navigate]);

  async function submit(v: EditorValues, andIssue: boolean) {
    let id = savedId;
    try {
      const input = toInput(type, v);
      if (!id && copy) input.copied_from_document_id = copy.from;
      const saved = await save.mutateAsync({ id, input });
      id = saved.id;
      setSavedId(saved.id);
      if (andIssue) {
        const manual = v.number.trim() !== "" && v.number.trim() !== next.data?.number ? v.number.trim() : undefined;
        await issue.mutateAsync({ id: saved.id, number: manual });
      }
      notifySuccess(andIssue ? "Doklad byl uložen." : "Koncept byl uložen.");
      reset(v);
      setLeaveTo(documentPath(companyId, saved.id));
    } catch (err) {
      apiErrorToFormErrors<EditorValues>(err, setError, {
        fallbackMessage: "Uložení se nepovedlo.",
        // No input shows the copy's source: its refusal goes to the alert.
        fieldMap: (f) => (f === "copied_from_document_id" ? "root.serverError" : f) as Path<EditorValues>,
      });
      // A draft saved before the issue failed is edited from now on.
      if (id && !doc) window.history.replaceState(null, "", `${documentPath(companyId, id)}/edit`);
    }
  }

  const busy = save.isPending || issue.isPending;
  const datePicker = (name: "issue_date" | "taxable_supply_date" | "due_date" | "expiration_date", label: string, required = false) => (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: "Povinné pole" } : undefined}
      render={({ field, fieldState }) => (
        <DatePicker
          name={field.name}
          ref={field.ref}
          onBlur={field.onBlur}
          label={label}
          required={required}
          value={civilToDayjs(field.value)}
          onChange={(d) => field.onChange(dayjsToCivil(d))}
          error={fieldState.error?.message}
        />
      )}
    />
  );
  const party = type === "purchase_invoice" ? "Dodavatel" : "Odběratel";

  const numberError = formState.errors.number?.message ?? (next.error ? "Pro tento rok není založená číselná řada." : undefined);
  const bd = contact.data?.billing_details;
  const dueShortcut = (days: number) => (
    <Button
      key={days}
      type="button"
      size="sm"
      weight="ghost"
      onClick={() => setValue("due_date", addDaysCivil(issueDate || today, days), { shouldDirty: true })}
    >
      {days} dní
    </Button>
  );

  return (
    <>
      <Page>
        <Page.Content>
          <FormProvider {...form}>
            <form noValidate onSubmit={handleSubmit((v) => submit(v, true))}>
              <Card>
                <Card.Header
                  title={
                    <>
                      {doc ? titles.edit : titles.create} <span className={styles.number}>{number}</span>
                    </>
                  }
                  subtitle={company.billing_details.business_name}
                  actions={
                    !editNumber && (
                      <Button type="button" size="sm" weight="ghost" onClick={() => setEditNumber(true)}>
                        změnit číslo
                      </Button>
                    )
                  }
                />
                <Card.Content>
                  <div className={styles.sections}>
                    {rootErrorMessage(formState.errors) && <Alert variant="err">{rootErrorMessage(formState.errors)}</Alert>}
                    {(editNumber || numberError) && (
                      <div className={styles.cols3}>
                        <Input label="Číslo dokladu" {...register("number")} error={numberError} hint="Prázdné nebo beze změny: další číslo řady." />
                      </div>
                    )}

                    <section className={styles.section}>
                      <h2 className={styles.heading}>{party}</h2>
                      <div className={styles.cols2}>
                        <Controller
                          name="contact_id"
                          control={control}
                          rules={type === "retail" ? undefined : { required: "Vyberte kontakt z adresáře" }}
                          render={({ field, fieldState }) => (
                            <ContactPicker companyId={companyId} label="Firma nebo osoba" value={field.value} onChange={field.onChange} error={fieldState.error?.message} />
                          )}
                        />
                        {bd && (
                          <div className={styles.partyCard}>
                            {[bd.street, [bd.post_code, bd.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || <Text variant="muted">bez adresy</Text>}
                            <br />
                            {[bd.ico && `IČO ${bd.ico}`, bd.dic && `DIČ ${bd.dic}`].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                      {type === "credit_note" && (
                        <Input
                          label="Důvod opravy"
                          required
                          {...register("credit_note_reason", { validate: (v) => v.trim() !== "" || "Povinné pole" })}
                          error={formState.errors.credit_note_reason?.message}
                        />
                      )}
                    </section>

                    <section className={styles.section}>
                      <h2 className={styles.heading}>Termíny</h2>
                      <div className={styles.cols3}>
                        {datePicker("issue_date", type === "retail" ? "Datum" : "Datum vystavení", true)}
                        {invoiceLike && vatPayer && datePicker("taxable_supply_date", "Datum zdan. plnění")}
                        {invoiceLike && type !== "retail" && (
                          <div className={styles.due}>
                            {datePicker("due_date", "Splatnost")}
                            <div className={styles.shortcuts}>{[7, 14, 30].map(dueShortcut)}</div>
                          </div>
                        )}
                        {!invoiceLike && datePicker("expiration_date", "Platnost do")}
                      </div>
                    </section>

                    <LineItemsEditor
                      companyId={companyId}
                      vatPayer={vatPayer}
                      defaultVatRate={defaultRate}
                      lineTotals={live.lineTotals}
                      totals={live.totals}
                      pending={live.pending}
                    />

                    <section className={styles.section}>
                    <h2 className={styles.heading}>Platba a poznámky</h2>
                    <div className={styles.cols3}>
                      <Select label="Způsob platby" {...register("payment_type")}>
                        {(Object.keys(PAYMENT_LABEL) as PaymentType[]).map((p) => (
                          <option key={p} value={p}>{PAYMENT_LABEL[p]}</option>
                        ))}
                      </Select>
                      <Select label="Měna" {...register("currency")}>
                        <option value="CZK">CZK</option>
                        <option value="EUR">EUR</option>
                      </Select>
                      {currency !== "CZK" ? (
                        <Controller
                          name="czk_rate"
                          control={control}
                          render={({ field, fieldState }) => (
                            <DecimalInput label="Kurz CZK" scale={3} suffix="Kč" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} hint="Kč za 1 jednotku měny (ČNB)." />
                          )}
                        />
                      ) : (type === "sales_invoice" || type === "sales_order") ? (
                        <Input label="Číslo objednávky" {...register("order_number")} />
                      ) : (
                        <span />
                      )}
                    </div>
                    {currency !== "CZK" && (type === "sales_invoice" || type === "sales_order") && (
                      <div className={styles.cols3}>
                        <Input label="Číslo objednávky" {...register("order_number")} />
                      </div>
                    )}
                    <div className={styles.cols2}>
                      <Textarea label="Text před položkami" rows={3} {...register("items_text_prefix")} />
                      <Textarea label="Text za položkami" rows={3} {...register("items_text_suffix")} />
                    </div>
                    </section>
                  </div>
                </Card.Content>

                <Card.Footer>
                  <div className={styles.footer}>
                    <Button type="button" variant="primary" weight="outline" disabled={busy} onClick={handleSubmit((v) => submit(v, false))}>
                      uložit koncept
                    </Button>
                    <Button type="submit" variant="primary" startIcon={<Send size={16} aria-hidden="true" />} loading={busy} disabled={busy}>
                      vystavit
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </form>
          </FormProvider>
        </Page.Content>
      </Page>
      <UnsavedChangesModal open={guard.blocked} onConfirm={guard.proceed} onClose={guard.cancel} />
    </>
  );
}

/** /c/:companyId/documents/new?type=, …/new?from=:documentId (a copy) and …/:documentId/edit */
export function DocumentEditorPage() {
  const { id: companyId } = useCurrentCompany();
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const doc = useDocument(companyId, documentId);
  const from = documentId ? null : searchParams.get("from");
  const copy = useDocumentCopy(companyId, from);

  if (documentId) {
    if (doc.isLoading) return <LoadingState />;
    // A failed background refetch keeps the data: the form stays.
    if (doc.error && !doc.data) return <ErrorState title="Doklad se nepodařilo načíst" error={doc.error} onRetry={doc.refetch} />;
    if (doc.data!.status !== "draft") {
      return (
        <Page>
          <Page.Content>
            <Alert variant="warn">Vystavený doklad už nelze měnit. Opravte ho dobropisem.</Alert>
          </Page.Content>
        </Page>
      );
    }
    return <Editor type={doc.data!.type} doc={doc.data} />;
  }
  if (from) {
    if (copy.isLoading) return <LoadingState />;
    if (copy.error && !copy.data) return <ErrorState title="Doklad se nepodařilo zkopírovat" error={copy.error} onRetry={copy.refetch} />;
    return <Editor key={from} type={copy.data!.type} copy={{ from, input: copy.data! }} />;
  }
  const t = searchParams.get("type") as DocumentType;
  return <Editor type={EDITABLE_TYPES.includes(t) ? t : "sales_invoice"} />;
}
